import { CollectionTypeChip } from "@/Collections/Collection/CollectionTypeChip";
import { renderPrincipal } from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import {
  cellStyles,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation";
import { capitalizeFirst } from "@/shared/text/capitalizeFirst";

import { CollectionRecord } from "./CollectionRecord";

const columnHelper = createColumnHelper<CollectionRecord>();

export const collectionsTableColumns = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: true,
    cellRenderer: (p: ICellRendererParams<CollectionRecord, string | null>) => {
      const record = p.data;
      if (!record) {
        return null;
      }
      if (!record.name) {
        return <span className={cellStyles.placeholder}>Untitled</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createCollectionRoute(record.collection_id)}
          className={cellStyles.primaryContent}
        >
          {record.name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("collection_id", {
    headerName: "ID",
    minWidth: 150,
    cellRenderer: (p: ICellRendererParams<CollectionRecord, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createCollectionRoute(id)}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("updated_by", {
    headerName: "Updated By",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("updated", {
    headerName: "Updated",
    minWidth: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    minWidth: 150,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("resource_type", {
    headerName: "Entity Type",
    minWidth: 140,
    sortable: true,
    valueFormatter: (p) => {
      const v = p.value;
      if (!v) {
        return "";
      }
      return `${capitalizeFirst(v)}s`;
    },
    cellRenderer: (
      p: ICellRendererParams<
        CollectionRecord,
        CollectionRecord["resource_type"]
      >,
    ) => {
      if (!p.value) {
        return null;
      }
      return <CollectionTypeChip resourceType={p.value} />;
    },
  }),
  columnHelper.accessor("resource_count", {
    headerName: "Entity Count",
    minWidth: 100,
    sortable: true,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<CollectionRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created By",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
];
